import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useFleets } from "../api/queries";
import type { Fleet } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { canSeeFleet } from "../auth/roleView";

const STORAGE_KEY = "comebye.fleetId";

interface FleetContextValue {
  fleets: Fleet[];
  /** The fleet currently selected in the fleet switcher, or undefined while
   * the list is still loading. */
  current: Fleet | undefined;
  currentId: string;
  setCurrentId: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

const FleetContext = createContext<FleetContextValue | undefined>(undefined);

/**
 * Owns the "which fleet am I looking at" selection. Every data view reads
 * `currentId` from here rather than fetching its own fleet list, so the
 * top-bar switcher and the routed content always agree.
 */
export function FleetProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  // A role with no fleet grants (billing_admin) never asks for the fleet list:
  // the request could only answer 403, and a failed fetch here would put an
  // error in the shell around a billing page that is working fine.
  const { data: fleets, isLoading, isError, error, refetch } = useFleets({ enabled: canSeeFleet(session?.role) });
  const [selectedId, setSelectedId] = useState<string>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!fleets || fleets.length === 0) return;
    const stillValid = fleets.some((f) => f.id === selectedId);
    if (!stillValid) {
      setSelectedId(fleets[0].id);
    }
    // Only re-run when the fetched list changes — selectedId changes are
    // handled by setCurrentId below, not by this reconciliation effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleets]);

  const setCurrentId = (id: string) => {
    setSelectedId(id);
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Non-fatal — selection still works for this session.
    }
  };

  const current = fleets?.find((f) => f.id === selectedId);

  const value = useMemo<FleetContextValue>(
    () => ({
      fleets: fleets ?? [],
      current,
      currentId: current?.id ?? "",
      setCurrentId,
      isLoading,
      isError,
      error,
      refetch,
    }),
    [fleets, current, isLoading, isError, error, refetch],
  );

  return <FleetContext.Provider value={value}>{children}</FleetContext.Provider>;
}

export function useFleet(): FleetContextValue {
  const ctx = useContext(FleetContext);
  if (!ctx) throw new Error("useFleet must be used within a FleetProvider");
  return ctx;
}
